/**
 * MS-CUSTOM: YouTube Short helper for snippets/video-youtube-short.liquid
 *
 * The page drives the player through the YouTube API: taps land on the poster button over the
 * player (play / pause) and on the sound button. One exception: when the browser refuses a start
 * from the page (iPhones only start sound from a tap inside the player), the play button lets the
 * next tap through to the player itself ([data-short-pass]); once it plays, the page takes over again.
 *
 * - a click-to-play Short starts with sound, an autoplay Short muted (sound button for sound)
 * - one spinner from the tap until it plays ([data-short-ready], [data-short-buffering]); if it can't
 *   start on its own, the play button shows instead
 * - only one click-to-play player stays loaded (phones run out of video decoders), and a Short that
 *   leaves the screen pauses; an autoplay Short only plays while on screen
 * - toggles .is-playing on <deferred-media> (keeps the poster on top while not playing)
 * - only one Short with sound at a time
 * - forces captions off unless the block enables them
 * - pauses just before the end so YouTube's end screen never shows
 * Loaded as a module, so it executes once even when several Shorts are on the page.
 */
(() => {
  const SELECTOR = '.video-block--short iframe.js-youtube';
  const END_OFFSET = 0.5;
  // Give up on a player that never answers (e.g. YouTube blocked): taps then go to the player
  const READY_TIMEOUT = 8000;
  // A start still buffering after START_TIMEOUT gets this much longer
  const BUFFERING_TIMEOUT = 4000;
  // A start (or sound-on) that hasn't happened by then was refused by the browser
  const START_TIMEOUT = 1500;
  const YT_ORIGIN = /^https:\/\/www\.youtube(-nocookie)?\.com$/;
  const players = new Map();

  const post = (iframe, payload) => iframe.contentWindow?.postMessage(JSON.stringify(payload), '*');
  const command = (iframe, func, args = []) => post(iframe, { event: 'command', func, args });
  const playerOf = (media) => {
    const iframe = media?.querySelector(':scope > iframe.js-youtube');
    return iframe ? players.get(iframe.contentWindow) : undefined;
  };
  const pause = (iframe) => {
    const media = iframe.closest('deferred-media');
    const state = players.get(iframe.contentWindow);
    if (state) state.pausedAt = Date.now();
    media?.pauseMedia ? media.pauseMedia() : command(iframe, 'pauseVideo');
  };
  // Horizon only announces playback on the first load (DeferredMedia.loadContent), so restarting an
  // already loaded Short would leave other videos running.
  const pauseOthers = (iframe) => {
    const own = iframe.closest('deferred-media');
    document.querySelectorAll('deferred-media[data-media-loaded]').forEach((media) => {
      if (media === own) return;
      const other = playerOf(media);
      if (other) other.pausedAt = Date.now();
      media.pauseMedia?.();
    });
  };

  /**
   * Back to the poster, without its player (the template stays, so a tap loads it again).
   * Like unloadVideo in slider-carousel.js, for Shorts outside of a slider as well.
   */
  const unload = (media) => {
    media.pauseMedia?.();
    for (const iframe of media.querySelectorAll(':scope > iframe')) {
      players.delete(iframe.contentWindow);
      iframe.remove();
    }
    for (const name of ['data-media-loaded', 'data-short-js', 'data-short-ready', 'data-short-buffering', 'data-short-pass']) {
      media.removeAttribute(name);
    }
    media.classList.remove('is-playing');
    media.querySelector(':scope > .deferred-media__poster-button')?.classList.remove('deferred-media__playing');
    media.isPlaying = false;
  };

  // Spinner while the player loads, starts or buffers before playing (see the snippet styles)
  const showLoading = (state) => {
    state.iframe
      .closest('deferred-media')
      ?.toggleAttribute('data-short-buffering', Boolean(state.startTimer) || state.playerState === 3);
  };

  const showSound = (state) => {
    const button = state.iframe.closest('deferred-media')?.querySelector(':scope > .video-short__sound');
    button?.setAttribute('aria-pressed', String(!state.muted));
  };

  // The next tap goes to the player itself (it then starts with sound, as a tap inside it is allowed to)
  const passTaps = (state, on) => state.iframe.closest('deferred-media')?.toggleAttribute('data-short-pass', on);

  const watchStart = (state, wait = START_TIMEOUT) => {
    clearTimeout(state.startTimer);
    state.startTimer = setTimeout(() => {
      state.startTimer = 0;
      // Still buffering (slow connection): wait a little longer
      if (state.playerState === 3 && wait === START_TIMEOUT) return watchStart(state, BUFFERING_TIMEOUT);
      if (state.playerState !== 1) passTaps(state, true);
      showLoading(state);
    }, wait);
    showLoading(state);
  };

  const play = (state) => {
    state.started = true;
    if (!state.muted) pauseOthers(state.iframe);
    command(state.iframe, 'playVideo');
    watchStart(state);
  };

  const toggle = (state) => {
    if (state.playerState === 1 || state.playerState === 3 || state.startTimer) {
      clearTimeout(state.startTimer);
      state.startTimer = 0;
      pause(state.iframe);
      // Show it paused right away, YouTube confirms a moment later
      state.iframe.closest('deferred-media')?.classList.remove('is-playing');
      showLoading(state);
      return;
    }
    play(state);
  };

  const setSound = (state, on) => {
    state.muted = !on;
    if (on) {
      state.unmutedAt = Date.now();
      command(state.iframe, 'unMute');
      command(state.iframe, 'setVolume', [100]);
      pauseOthers(state.iframe);
    } else {
      command(state.iframe, 'mute');
    }
    showSound(state);
  };

  // A Short that leaves the screen (scrolled or swiped away) pauses; an autoplay Short starts and
  // resumes only while on screen
  const visibility = new IntersectionObserver(
    (entries) => {
      for (const { target, intersectionRatio } of entries) {
        const state = playerOf(target);
        if (!state) continue;
        state.visible = intersectionRatio >= 0.5;
        if (!state.ready) continue;
        const playing = state.playerState === 1 || state.playerState === 3 || state.startTimer;
        if (!state.visible && playing) {
          state.resume = target.hasAttribute('autoplay');
          clearTimeout(state.startTimer);
          state.startTimer = 0;
          pause(state.iframe);
          showLoading(state);
        } else if (state.visible && state.resume) {
          state.resume = false;
          play(state);
        }
      }
    },
    { threshold: 0.5 }
  );

  const attach = (iframe) => {
    if (iframe.dataset.shortBound) return;
    iframe.dataset.shortBound = 'true';
    const media = iframe.closest('deferred-media');
    if (!media) return;
    const autoplay = media.hasAttribute('autoplay');
    // Autoplay: the player is in the markup from the start. Mark it loaded like a clicked Short, so
    // the poster covers it until it plays and a tap never makes Horizon add a second player.
    if (autoplay && !media.hasAttribute('data-media-loaded')) {
      media.setAttribute('data-media-loaded', 'true');
      media.querySelector(':scope > .deferred-media__poster-button')?.classList.add('deferred-media__playing');
    }
    // A new click-to-play player: unload the others
    if (!autoplay) {
      document.querySelectorAll('.video-block--short[data-media-loaded]:not([autoplay])').forEach((other) => {
        if (other !== media) unload(other);
      });
    }
    // Enables the poster-overlay CSS in snippets/video-youtube-short.liquid
    media.setAttribute('data-short-js', '');
    media.removeAttribute('data-short-pass');

    // The embed URL is muted for autoplay Shorts only, and never autoplays: the page starts it
    const state = {
      iframe,
      ready: false,
      started: false,
      frozen: false,
      duration: 0,
      time: 0,
      playerState: -1,
      muted: autoplay,
      visible: true,
      resume: false,
      startTimer: 0,
      unmutedAt: 0,
      pausedAt: 0,
    };
    showSound(state);

    const listen = () => {
      players.set(iframe.contentWindow, state);
      media.removeAttribute('data-short-ready');
      clearTimeout(state.readyTimer);
      state.readyTimer = setTimeout(() => {
        media.setAttribute('data-short-ready', '');
        passTaps(state, true);
      }, READY_TIMEOUT);
      let tries = 0;
      const timer = setInterval(() => {
        if (state.ready || ++tries > 20) return clearInterval(timer);
        post(iframe, { event: 'listening', id: 1, channel: 'widget' });
      }, 250);
    };
    iframe.addEventListener('load', () => {
      state.ready = false;
      state.started = false;
      listen();
    });
    listen();
    visibility.observe(media);
  };

  window.addEventListener('message', (event) => {
    if (!YT_ORIGIN.test(event.origin)) return;
    const state = players.get(event.source);
    if (!state) return;

    let data;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    const media = state.iframe.closest('deferred-media');
    if (!state.ready) {
      clearTimeout(state.readyTimer);
      media?.setAttribute('data-short-ready', '');
    }
    state.ready = true;
    const info = data?.info && typeof data.info === 'object' ? data.info : {};

    if (typeof info.duration === 'number') state.duration = info.duration;
    if (typeof info.currentTime === 'number') state.time = info.currentTime;
    if (typeof info.muted === 'boolean') {
      state.muted = info.muted;
      showSound(state);
    }

    let restart = false;
    let freeze = false;

    if (typeof info.playerState === 'number') {
      const wasPlaying = state.playerState === 1;
      state.playerState = info.playerState;
      if (info.playerState === 1 || info.playerState === 3) state.started = true;
      if (info.playerState === 1) {
        clearTimeout(state.startTimer);
        state.startTimer = 0;
        passTaps(state, false);
        // Only one Short with sound at a time (a muted autoplay Short doesn't pause the others)
        if (!wasPlaying && !state.muted) pauseOthers(state.iframe);
      }
      // 2 = paused right after the sound was turned on, without anyone asking: the browser refused
      // the sound (iPhones, no tap inside the player). The next tap goes to the player instead.
      if (info.playerState === 2 && Date.now() - state.unmutedAt < START_TIMEOUT && state.pausedAt < state.unmutedAt) {
        passTaps(state, true);
      }
      if (media) media.isPlaying = info.playerState === 1 || info.playerState === 3;
      // 1 = playing; ignore 3 (buffering) so the poster doesn't flash
      if (info.playerState !== 3) {
        media?.classList.toggle('is-playing', info.playerState === 1);
      }
      // Captions off (unless enabled in the block): YouTube loads the captions
      // module a moment after playback starts, so repeat the command a few times
      if (info.playerState === 1 && !state.captionsOff && !state.iframe.closest('.video-block--short-captions')) {
        state.captionsOff = true;
        const captionsOff = () => {
          command(state.iframe, 'setOption', ['captions', 'track', {}]);
          command(state.iframe, 'unloadModule', ['captions']);
          command(state.iframe, 'unloadModule', ['cc']);
        };
        [0, 500, 1500, 3000].forEach((delay) => setTimeout(captionsOff, delay));
      }
      restart = info.playerState === 1 && state.frozen;
      freeze = info.playerState === 0 && Boolean(state.duration);
    }

    // First answer from the player: start it (an autoplay Short only once it is on screen)
    if (!state.started) {
      if (state.visible || !media?.hasAttribute('autoplay')) play(state);
      else state.resume = true;
    }
    showLoading(state);

    if (restart) {
      state.frozen = false;
      command(state.iframe, 'seekTo', [0, true]);
      return;
    }
    if (freeze) {
      state.frozen = true;
      command(state.iframe, 'seekTo', [Math.max(state.duration - END_OFFSET, 0), true]);
      pause(state.iframe);
      return;
    }
    if (!state.frozen && state.playerState === 1 && state.duration && state.time >= state.duration - END_OFFSET) {
      state.frozen = true;
      pause(state.iframe);
    }
  });

  // Taps on a loaded Short: the poster plays / pauses, the sound button toggles the sound.
  // Window capture runs before the theme's delegated on:click handlers (document capture), so the
  // poster never asks Horizon to load the player a second time.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const media = target.closest('.video-block--short[data-short-js][data-media-loaded]');
      const sound = target.closest('.video-short__sound');
      if (!media || !(sound || target.closest('.deferred-media__poster-button'))) return;
      // A Short in a slide that is out of view: the slider brings the slide in first
      if (media.closest('slideshow-slide[aria-hidden="true"]')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const state = playerOf(media);
      // Still loading (spinner): nothing to control yet
      if (!state?.ready) return;
      if (sound) setSound(state, state.muted);
      else toggle(state);
    },
    { capture: true }
  );

  const scan = (root = document) => root.querySelectorAll(SELECTOR).forEach(attach);
  scan();
  new MutationObserver((mutations) => {
    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.nodeType !== 1) continue;
        node.matches(SELECTOR) ? attach(node) : scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
